// src/screens/admin/productList/ProductList.jsx

import { useState, useEffect, useContext } from "react";
import {
  Plus,
  Eye,
  Edit2,
  Trash2,
  Search,
  ImageOff,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AddProduct from "../../../subscreen/admin/addProduct/AddProduct";
import EditProduct from "../../../subscreen/admin/editProduct/EditProduct";
import "../../../assets/css/admin/adminproductlist/AdminProductList.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const ProductList = () => {
  const { baseUrl, baseUrlForFile, authToken } = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [idToUpdateDelete, setIdToUpdateDelete] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    viewAllProducts();
  }, []);

  const viewAllProducts = () => {
    axios
      .get(`${baseUrl}/admin/products/viewAllProducts`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setProducts(response.data.products);
      })
      .catch((error) => {
        console.log("error:", error);
      });
  };

  // Search filter
  const visibleProducts = (() => {
    const base = products || [];
    const q = searchQuery.trim().toLowerCase();
    return q
      ? base.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.adcategory?.name?.toLowerCase().includes(q),
        )
      : base;
  })();

  // ── Function to Delete product ──
  const deleteProduct = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this product?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);

        axios
          .post(`${baseUrl}/admin/products/deleteProduct`, formData, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            console.log(response.data);
            viewAllProducts();
          })
          .catch((error) => {
            console.log(error);
          });
      }
    });
  };

  // ── Function to toggle Popular status ──
  const togglePopular = (id, currentStatus) => {
    const formData = new FormData();
    formData.append("id", id);
    formData.append("is_popular", !currentStatus ? 1 : 0);

    axios
      .post(`${baseUrl}/admin/products/toggleProduct`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success) {
          viewAllProducts();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ── Function to update local product state after a successful edit ──
  const handleProductEdited = (updatedProduct) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === updatedProduct.id ? { ...p, ...updatedProduct } : p,
      ),
    );
  };

  return (
    <div className="pl-container">
      {/* ── Header ── */}
      <div className="pl-header">
        <div>
          <h1 className="pl-title">Product List</h1>
          <p className="pl-subtitle">
            Manage and monitor your furniture products
          </p>
        </div>
        <button className="pl-add-btn" onClick={() => setIsAddOpen(true)}>
          <Plus size={16} /> Add Product
        </button>
      </div>

      {/* ── Controls ── */}
      <div className="pl-controls">
        <div className="pl-search">
          <Search size={15} className="ad-pl-search-icon" />
          <input
            type="text"
            className="pl-search-input"
            placeholder="Search by name or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="pl-table-container">
        {visibleProducts.length === 0 ? (
          <div className="pl-empty">
            <span className="pl-empty-icon">📦</span>
            <h3>No products found</h3>
            <p>Try adjusting your search</p>
          </div>
        ) : (
          <table className="pl-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Price</th>
                <th>Category</th>
                <th>Popular</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleProducts.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="pl-product">
                      {p.productimages[0] ? (
                        <img
                          src={`${baseUrlForFile}/${p.productimages[0].image_path}`}
                          alt={p.name}
                          className="pl-product-img"
                        />
                      ) : (
                        <ImageOff size={20} />
                      )}
                      <div>
                        <div className="pl-product-name">{p.name}</div>
                        <div className="pl-product-size">Size: {p.size}</div>
                      </div>
                    </div>
                  </td>
                  <td className="pl-price">PKR {p.price.toLocaleString()}</td>
                  <td>
                    <span className="pl-badge">{p.adcategory.name}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`pl-popular-toggle ${p.is_popular ? "active" : ""}`}
                      onClick={() => togglePopular(p.id, p.is_popular)}
                      title={
                        p.is_popular ? "Mark as not popular" : "Mark as popular"
                      }
                      aria-pressed={!!p.is_popular}
                    >
                      {p.is_popular ? (
                        <ToggleRight size={24} />
                      ) : (
                        <ToggleLeft size={24} />
                      )}
                    </button>
                  </td>
                  <td>
                    <div className="pl-actions">
                      <button
                        className="pl-action-btn pl-view"
                        title="View"
                        onClick={() => navigate(`/admin/product-list/${p.id}`)}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        className="pl-action-btn pl-edit"
                        title="Edit"
                        onClick={() => setIdToUpdateDelete(p.id)}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="pl-action-btn pl-delete"
                        title="Delete"
                        onClick={() => deleteProduct(p.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modals ── */}
      {isAddOpen && (
        <AddProduct
          isOpen
          onClose={() => setIsAddOpen(false)}
          mode="add"
          product={null}
          viewAllProducts={viewAllProducts}
        />
      )}
      {idToUpdateDelete && (
        <EditProduct
          isOpen
          onClose={() => setIdToUpdateDelete(null)}
          product={products.find((p) => p.id === idToUpdateDelete)}
          onEdit={handleProductEdited}
          viewAllProducts={viewAllProducts}
        />
      )}
    </div>
  );
};

export default ProductList;
